import { useEffect, useState } from 'react';
import { Envelope, Eye, Check, Archive } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MESSAGE_STATUSES = [
    { value: 'nouveau', label: 'Nouveau', color: 'bg-blue-500/20 text-blue-400' },
    { value: 'lu', label: 'Lu', color: 'bg-yellow-500/20 text-yellow-400' },
    { value: 'pris_en_compte', label: 'Pris en compte', color: 'bg-green-500/20 text-green-400' },
    { value: 'archive', label: 'Archivé', color: 'bg-gray-500/20 text-gray-400' },
];

const RAISON_LABELS = {
    devis: 'Demande de devis',
    produit: 'Demande de produit',
    partenariat: 'Partenariat',
    autre: 'Autre'
};

export default function AdminMessagesPage() {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState(null);

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async () => {
        try {
            const response = await axios.get(`${API}/messages`);
            setMessages(response.data);
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, statut) => {
        try {
            await axios.put(`${API}/messages/${id}/status`, { statut });
            toast.success('Statut mis à jour');
            fetchMessages();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const openMessage = (msg) => {
        setSelectedMessage(msg);
        if (msg.statut === 'nouveau') {
            updateStatus(msg.id, 'lu');
        }
    };

    const getStatusBadge = (statut) => {
        const status = MESSAGE_STATUSES.find(s => s.value === statut);
        return status ? <span className={`px-2 py-1 rounded text-xs ${status.color}`}>{status.label}</span> : statut;
    };

    const formatDate = (date) => new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    return (
        <div data-testid="admin-messages-page">
            <div className="mb-6">
                <h1 className="font-heading text-2xl font-medium text-white">Messages</h1>
                <p className="text-mmd-silver/70 text-sm">Gérez les messages de contact</p>
            </div>

            {loading ? (
                <div className="space-y-4">
                    {[1, 2, 3].map(i => <div key={i} className="bg-mmd-surface-dark rounded-lg p-4 animate-pulse h-20" />)}
                </div>
            ) : messages.length === 0 ? (
                <div className="text-center py-12 text-mmd-silver/50">
                    <Envelope size={48} className="mx-auto mb-2" />
                    <p>Aucun message</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {messages.map(msg => (
                        <div
                            key={msg.id}
                            className={`bg-mmd-surface-dark border rounded-lg p-4 cursor-pointer transition-colors hover:border-mmd-gold/50 ${
                                msg.statut === 'nouveau' ? 'border-mmd-gold/30' : 'border-white/10'
                            }`}
                            onClick={() => openMessage(msg)}
                            data-testid={`message-${msg.id}`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        {msg.statut === 'nouveau' && <span className="w-2 h-2 bg-mmd-gold rounded-full" />}
                                        <h3 className="font-medium text-white truncate">
                                            {msg.type_personne === 'physique' ? `${msg.prenom} ${msg.nom}` : msg.ninea_rc_siret}
                                        </h3>
                                        {getStatusBadge(msg.statut)}
                                    </div>
                                    <p className="text-sm text-mmd-silver/60 truncate">{msg.email} - {msg.telephone}</p>
                                    <p className="text-xs text-mmd-gold mt-1">{RAISON_LABELS[msg.raison] || msg.raison}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-mmd-silver/40">{formatDate(msg.created_at)}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
                <DialogContent className="sm:max-w-lg">
                    {selectedMessage && (
                        <>
                            <DialogHeader>
                                <DialogTitle>
                                    Message de {selectedMessage.type_personne === 'physique' 
                                        ? `${selectedMessage.prenom} ${selectedMessage.nom}` 
                                        : selectedMessage.ninea_rc_siret}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="mt-4 space-y-4">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <p className="text-muted-foreground">Email</p>
                                        <a href={`mailto:${selectedMessage.email}`} className="text-mmd-gold hover:underline">
                                            {selectedMessage.email}
                                        </a>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Téléphone</p>
                                        <a href={`tel:${selectedMessage.telephone}`} className="text-mmd-gold hover:underline">
                                            {selectedMessage.telephone}
                                        </a>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Raison</p>
                                        <p>{RAISON_LABELS[selectedMessage.raison] || selectedMessage.raison}</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Date</p>
                                        <p>{formatDate(selectedMessage.created_at)}</p>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-muted-foreground text-sm mb-2">Message</p>
                                    <div className="bg-muted/50 rounded-lg p-4 text-sm whitespace-pre-wrap">
                                        {selectedMessage.message}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 pt-4 border-t">
                                    <p className="text-sm text-muted-foreground mr-2">Statut:</p>
                                    <Select value={selectedMessage.statut} onValueChange={(v) => {
                                        updateStatus(selectedMessage.id, v);
                                        setSelectedMessage({...selectedMessage, statut: v});
                                    }}>
                                        <SelectTrigger className="w-40" data-testid="message-status-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {MESSAGE_STATUSES.map(s => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
